/**
 * The outside systems an item — or an objective — is linked to
 * (entity_external_ids, migration 402): Jira keys, Miro cards, Linear issues
 * and initiatives… with their links and last sync. Read by the item page's
 * "Linked in" line and its Linked systems section, and by an objective's
 * "Where this objective lives". Re-reads when a link is made, synced or
 * removed (notifyExternalIdsChanged).
 */

import { useCallback, useEffect, useState } from 'react';
import { EXTERNAL_IDS_CHANGED_EVENT, getExternalIds } from '@/lib/connectors';

export interface ItemExternalLink {
  connectorType: string;
  externalId: string;
  /** The key a person recognizes (PRI-12) when externalId is an opaque id; null otherwise. */
  externalLabel: string | null;
  externalUrl: string | null;
  lastSyncedAt: string | null;
  /** For a strategy object: what the record there last said, per field of ours. */
  externalValues?: Record<string, unknown>;
}

/** What to show for a link: its label when it has one, else its id. */
export function externalLinkDisplayKey(link: Pick<ItemExternalLink, 'externalId' | 'externalLabel'>): string {
  return link.externalLabel?.trim() || link.externalId;
}

/** null while loading. */
export function useItemExternalLinks(workspaceId: string | null | undefined, itemId: string): ItemExternalLink[] | null {
  return useExternalLinks(workspaceId, 'item', itemId);
}

/** The links of any linked record here, by its type (item, objective). null while loading. */
export function useExternalLinks(
  workspaceId: string | null | undefined,
  entityType: string,
  internalId: string,
): ItemExternalLink[] | null {
  const [links, setLinks] = useState<ItemExternalLink[] | null>(null);

  const read = useCallback(
    (live: () => boolean, showLoading: boolean) => {
      if (!workspaceId) {
        setLinks([]);
        return;
      }
      if (showLoading) setLinks(null);
      getExternalIds(workspaceId, entityType, internalId)
        .then((rows) => live() && setLinks(rows))
        .catch(() => live() && setLinks([]));
    },
    [workspaceId, entityType, internalId],
  );

  useEffect(() => {
    let live = true;
    read(() => live, true);
    const onChanged = (e: Event) => {
      const detail = (e as CustomEvent<{ entityType?: string; internalId?: string }>).detail;
      if (detail?.entityType === entityType && detail?.internalId === internalId) read(() => live, false);
    };
    window.addEventListener(EXTERNAL_IDS_CHANGED_EVENT, onChanged);
    return () => {
      live = false;
      window.removeEventListener(EXTERNAL_IDS_CHANGED_EVENT, onChanged);
    };
  }, [read, entityType, internalId]);

  return links;
}
